import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, Tag as TagIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogTagsList = () => {
  const [tags, setTags] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cms/blog-tags`);
      setTags(r.data);
    } catch {
      toast.error("Failed to load tags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.name) return toast.error("Name is required");
    try {
      if (editing.id) {
        await axios.put(`${API}/cms/blog-tags/${editing.id}`, editing);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/cms/blog-tags`, editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tag? Posts with it will lose the tag.")) return;
    await axios.delete(`${API}/cms/blog-tags/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/app/website/blog" className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Blog Tags</h1>
            <p className="text-sm text-slate-500">Taxonomies shown at /blog/tag/:slug</p>
          </div>
        </div>
        <button onClick={() => setEditing({ name: "", slug: "" })} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="new-tag-btn">
          <Plus size={14} /> New tag
        </button>
      </header>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : tags.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No tags yet. Create your first one.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <div key={t.id} className="group inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm" data-testid={`tag-row-${t.slug}`}>
              <TagIcon size={12} className="text-slate-400" />
              <span className="font-medium text-slate-800">{t.name}</span>
              <span className="text-xs text-slate-400 font-mono">/{t.slug}</span>
              <button onClick={() => setEditing(t)} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-100 rounded" data-testid={`edit-tag-${t.slug}`}><Edit2 size={11} /></button>
              <button onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`delete-tag-${t.slug}`}><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{editing.id ? "Edit tag" : "New tag"}</h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" data-testid="tag-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Slug <span className="text-xs text-slate-400">(auto if blank)</span></label>
              <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-slate-900 hover:bg-prepify-600 text-white rounded-lg font-medium" data-testid="save-tag-btn"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogTagsList;
